import React, { useEffect, useImperativeHandle, useRef } from "react";
// The FocusZone component is a generic component which implements a container that is a single tab stop
// ensuring that content within are not picked up as individual tab stops, even if they would otherwise be.
//
// - previousFocusableElement/nextFocusableElement are passed in to define the tabstop before/after the FocusZone
// - firstZoneFocusableElement is used to set the child element within the container to receive focus when the FocusZone receives focus
// - customOnFocusZoneKeyDown allows consumers to add special key handling within the FocusZone (i.e. handle arrow keys to move focus
//   within the FocusZone.
export const FocusZone = React.forwardRef((props, ref) => {
    const { children, previousFocusableElement, nextFocusableElement, firstZoneFocusableElement, customOnFocusZoneKeyDown } = props;
    const focusZoneRef = useRef(null);
    const isFocusFromPointerDownPending = useRef(false);
    const isTabPending = useRef(false);
    function getElementFromFunctionOrElement(functionOrElement) {
        return functionOrElement instanceof Function ? functionOrElement === null || functionOrElement === void 0 ? void 0 : functionOrElement() : functionOrElement;
    }
    function onFocusZonePointerDown(event) {
        isFocusFromPointerDownPending.current = true;
    }
    function onFocusZonePointerUp(event) {
        isFocusFromPointerDownPending.current = false;
    }
    function onFocusZoneFocus(event) {
        var _a;
        if (!isFocusFromPointerDownPending.current &&
            isTabPending.current &&
            !((_a = focusZoneRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget))) {
            firstZoneFocusableElement === null || firstZoneFocusableElement === void 0 ? void 0 : firstZoneFocusableElement.focus();
        }
        isFocusFromPointerDownPending.current = false;
    }
    function onFocusZoneBlur(event) {
        isFocusFromPointerDownPending.current = false;
    }
    function onFocusZoneKeyDown(event) {
        // First allow customOnFocusZoneKeyDown to handle any events.
        customOnFocusZoneKeyDown === null || customOnFocusZoneKeyDown === void 0 ? void 0 : customOnFocusZoneKeyDown(event);
        if (!event.defaultPrevented) {
            if (event.key === "Tab" && event.shiftKey) {
                // Handle "Shift" + "Tab" key combo by skipping to previous focusable element outside of the FocusZone, the settings button.
                // unless "Enter" or "Space" were previously used to enter the FocusZone
                const previousFocusableElementVar = getElementFromFunctionOrElement(previousFocusableElement);
                if (previousFocusableElementVar) {
                    previousFocusableElementVar.focus();
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
            else if (event.key === "Tab") {
                // Handle "Tab" key by skipping to next focusable element outside of the FocusZone, the "Add widgets" button
                // unless "Enter" or "Space" were previously used to enter the FocusZone.
                const nextFocusableElementVar = getElementFromFunctionOrElement(nextFocusableElement);
                if (nextFocusableElementVar) {
                    nextFocusableElementVar.focus();
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        }
    }
    // Track whether a Tab/Shift+Tab interaction is pending, focus should be moved to the firstZoneFocusableElement only when focus enters
    // the FocusZone from outside while a Tab/Shift+Tab interaction is pending. Tab/Shift+Tab interaction is no longer pending once a focus
    // event has been processed.
    useEffect(() => {
        function handleKeyDown(event) {
            // Don't consider tabs within shadowRoot, as we don't receive focus change events from inside shadowRoot and tabs within shadowRoot should not
            // be tracked as pending tab interactions within Widgets.
            if (event.key === "Tab" && !(document.activeElement && document.activeElement.shadowRoot)) {
                isTabPending.current = true;
            }
        }
        function handleFocusIn(event) {
            isTabPending.current = false;
        }
        // Handle on capture phase, to guarantee this is handled before any focusin events that occur due to keyDown handlers while this event is bubbling.
        document.addEventListener("keydown", handleKeyDown, true);
        document.addEventListener("focusin", handleFocusIn);
        return function cleanup() {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("focusin", handleFocusIn);
        };
    }, []);
    useImperativeHandle(ref, () => ({
        focusZone: focusZoneRef.current,
    }));
    return (React.createElement("div", { ref: focusZoneRef, onPointerDown: onFocusZonePointerDown, onPointerUp: onFocusZonePointerUp, onFocus: onFocusZoneFocus, onBlur: onFocusZoneBlur, onKeyDown: onFocusZoneKeyDown, tabIndex: -1 }, children));
});
